/**
 * Период одной кнопкой: готовые окна и календарь месяца.
 *
 * Копия `supply/web/src/components/DateRangePicker.tsx` (разметка владельца
 * 30.09.26 на `/pnl`: «возьми в supply нормальный дейтпикер»). Пара
 * `input type="date"` занимала тулбар вдвое, а её календарь браузер рисует
 * системным попапом — в тёмной теме белым. Окна свои: P&L смотрит назад и
 * считает месяцами, поэтому «прошлый месяц», «квартал», «год».
 *
 * Правка применяется вторым кликом, без кнопки «применить».
 */
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { RangeCalendar } from "@/components/RangeCalendar";
import { isValidDate, iso } from "@/lib/dates";

export type Окно = { label: string; hint: string; from: string; to: string };

/** Окна отчётного периода. Считаются на каждый рендер: вкладку держат днями. */
export function отчётныеОкна(): Окно[] {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const q = m - (m % 3);
  return [
    { label: "этот месяц", hint: "с первого по последнее число", from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) },
    { label: "прошлый месяц", hint: "весь прошлый месяц", from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) },
    { label: "этот квартал", hint: "три месяца квартала", from: iso(new Date(y, q, 1)), to: iso(new Date(y, q + 3, 0)) },
    { label: "этот год", hint: "с января по декабрь", from: `${y}-01-01`, to: `${y}-12-31` },
    { label: "прошлый год", hint: `весь ${y - 1} год`, from: `${y - 1}-01-01`, to: `${y - 1}-12-31` },
  ];
}

/** «24.09», а у чужого года «24.09.25». */
function краткая(value: string): string {
  const d = value.slice(8, 10);
  const m = value.slice(5, 7);
  const y = value.slice(0, 4);
  return y === String(new Date().getFullYear()) ? `${d}.${m}` : `${d}.${m}.${y.slice(2)}`;
}

/** Подпись кнопки: узнанное окно — своим именем, иначе даты. */
function подпись(from: string, to: string, список: Окно[]): string {
  if (!from && !to) return "всё время";
  const узнано = список.find((o) => o.from === from && o.to === to);
  if (узнано) return узнано.label;
  if (!to) return `с ${краткая(from)}`;
  if (!from) return `по ${краткая(to)}`;
  return `${краткая(from)} — ${краткая(to)}`;
}

export function DateRangePicker({
  label,
  from: fromRaw,
  to: toRaw,
  onChange,
  windows,
}: {
  label: string;
  /** Начало включительно, `YYYY-MM-DD`; пустая строка — без нижней границы. */
  from: string;
  /** Конец включительно; пустая строка — без верхней границы. */
  to: string;
  onChange: (from: string, to: string) => void;
  windows?: Окно[];
}) {
  // Некалендарная граница из адреса считается незаданной.
  const from = isValidDate(fromRaw) ? fromRaw : "";
  const to = isValidDate(toRaw) ? toRaw : "";

  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const список = windows ?? отчётныеОкна();

  // Клик мимо и Escape закрывают попап; `mousedown` — чтобы щелчок по
  // соседнему отбору и закрыл календарь, и сработал сам.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const выбрать = (nextFrom: string, nextTo: string) => {
    onChange(nextFrom, nextTo);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative" data-annot={`отбор ${label}`}>
      <button
        type="button"
        onClick={() => setOpen((было) => !было)}
        title={from || to ? `${from || "…"} — ${to || "…"} включительно` : "без отбора по дате"}
        className={
          "flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[12.5px] " +
          (open ? "border-border-strong" : "border-border hover:border-border-strong")
        }
      >
        <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">
          {label}
        </span>
        <span className="truncate px-2 tabular-nums text-fg">{подпись(from, to, список)}</span>
        <ChevronDown size={13} className="mr-1.5 ml-auto flex-none text-fg-faint" />
      </button>

      {open ? (
        <div className="absolute top-[34px] left-0 z-40 w-[264px] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg">
          <div className="py-1">
            {список.map((окно) => (
              <Строка
                key={окно.label}
                label={окно.label}
                title={окно.hint}
                on={окно.from === from && окно.to === to}
                onClick={() => выбрать(окно.from, окно.to)}
              />
            ))}
            <Строка label="всё время" title="снять отбор по дате" on={!from && !to} onClick={() => выбрать("", "")} />
          </div>
          <div className="border-t border-border">
            <RangeCalendar from={from} to={to} onPick={выбрать} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Копия адреса с другим периодом `date_from`/`date_to`; пусто — без границы. */
export function withPeriod(params: URLSearchParams, from: string, to: string): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [key, value] of [["date_from", from], ["date_to", to]] as const) {
    if (value) next.set(key, value);
    else next.delete(key);
  }
  return next;
}

function Строка(props: { label: string; title: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      title={props.title}
      onClick={props.onClick}
      className={
        "flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[12.5px] hover:bg-bg-hover " +
        (props.on ? "text-accent-text" : "text-fg")
      }
    >
      <span className="truncate">{props.label}</span>
      {props.on ? <Check size={12} className="ml-auto flex-none" /> : null}
    </button>
  );
}
